import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  revealLeft,
  revealRight,
  Eyebrow,
  GoldButton,
  EASE_LUXE,
  VIEWPORT,
} from "./primitives";
import { useLanguage } from "@/i18n/LanguageContext";

// The signature "writes itself" once the block is in view.
const signatureReveal = {
  hidden: { clipPath: "inset(0 100% 0 0)" },
  visible: {
    clipPath: "inset(0 0% 0 0)",
    transition: { duration: 1.1, delay: 0.4, ease: EASE_LUXE },
  },
};

export const FounderSection = ({ registerTo }) => {
  const { t } = useLanguage();
  return (
    <section
      id="fondateur"
      data-testid="founder-section"
      className="relative scroll-mt-28 py-14 md:py-32 border-t border-ink/5 bg-surface-muted overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-5 md:px-12 grid lg:grid-cols-[2fr_3fr] gap-12 lg:gap-20 items-start">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={revealLeft}
        >
          <div className="relative">
            <div
              aria-hidden="true"
              className="glow-warm pointer-events-none absolute left-1/2 top-1/2 aspect-square h-[130%] -translate-x-1/2 -translate-y-1/2"
            />
            <div className="absolute -inset-3 border border-gold/20" />
            <img
              src="/images/nathan-de-ridder.jpg"
              alt="Nathan De Ridder, fondateur de Provena"
              loading="lazy"
              className="relative w-full aspect-[3/4] object-cover"
            />
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={revealRight}
        >
          <Eyebrow>{t("founder.eyebrow")}</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight mb-8">
            {t("founder.title")}
          </h2>
          <div className="space-y-5 text-base md:text-lg text-ink-muted leading-relaxed">
            <p>{t("founder.p1")}</p>
            <p>{t("founder.p2")}</p>
          </div>
          <div className="mt-10">
            <span
              aria-hidden="true"
              className="mb-4 block h-px w-16 bg-gold/50"
            />
            <motion.p
              variants={signatureReveal}
              className="font-serif italic text-gold-ink text-xl md:text-2xl leading-none"
            >
              {t("founder.signature")}
            </motion.p>
            <p className="mt-3 text-[11px] tracking-[0.28em] uppercase text-ink-subtle">
              {t("founder.role")}
            </p>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <GoldButton to={registerTo} testId="founder-cta-button" track="founder">
              {t("finalCta.register")}
            </GoldButton>
            {/* Le portrait ci-dessus énonce une conviction ; la page À propos
                raconte la scène qui l'a fait naître. */}
            <Link
              to="/a-propos"
              data-testid="founder-read-story"
              className="link-underline text-xs tracking-[0.18em] uppercase text-ink-muted hover:text-gold-ink transition-colors duration-300"
            >
              {t("founder.readStory")}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FounderSection;
